// Constants shared by API and Web. Source of truth for enum-like vocab.
export const INTAKE_CLASSES = [
    'external_import',
    'local_import',
    'web_clipping',
    'structured_note',
    'rough_gathering',
    'scratchpad',
    'brain_dump',
];
export const DOCUMENT_STATUSES = [
    'captured',
    'parsed',
    'enriched',
    'reviewed',
    'promoted',
    'archived',
];
export const DOCUMENT_HEALTH = ['healthy', 'warning', 'degraded', 'invalid'];
export const PROCESSING_INTENTS = [
    'format', 'grammar', 'restructure', 'summarize', 'classify',
    'keyword_enrich', 'tag_enrich', 'link_internal',
    'validate_internal', 'validate_external',
    'research', 'deep_research', 'compare', 'crawl',
    'extract_tasks', 'extract_commands', 'extract_products',
    'generate_report', 'generate_spec', 'generate_runbook',
    'split_document', 'merge_candidates', 'promote_to_kb',
];
export const PROCESSING_DEPTHS = ['light', 'standard', 'deep', 'exhaustive'];
export const EXTERNAL_RESEARCH = ['blocked', 'allowed', 'required'];
export const PROCESSING_STATUSES = [
    'none', 'requested', 'queued', 'running',
    'blocked', 'completed', 'failed', 'cancelled', 'superseded',
];
export const FILE_TASK_STATUSES = [
    'queued', 'started', 'complete', 'failed', 'cancelled',
];
export const LINK_CONTEXTS = [
    'source', 'reference', 'output', 'evidence', 'requirement', 'attachment',
];
export const DEFAULT_WORK_STATUSES = [
    'backlog', 'spec_needed',
    'todo', 'ready', 'waiting_on_decision',
    'in_progress', 'in_review', 'needs_iteration', 'blocked',
    'done', 'verified',
    'cancelled', 'deferred',
];
export const WORKFLOW_STAGES = [
    'backlog', 'unstarted', 'started', 'completed', 'cancelled',
];
export const WORK_PRIORITIES = ['low', 'medium', 'high', 'critical'];
/**
 * Work-item flavours. The first four are the classic agile types; 'adr' and
 * 'troubleshooting' are knowledge-style records that some teams want as
 * first-class types instead of labels on a generic task.
 */
export const WORK_ITEM_TYPES = ['task', 'issue', 'epic', 'feature', 'adr', 'troubleshooting'];
/**
 * Soft work-item ↔ work-item link contexts. `dependencyIds` covers strict
 * "must be done before this can move forward" semantics with a workflow
 * guard; this enum covers everything else. Each pair below has a mirror
 * direction the API auto-creates so both ends of the link can find each
 * other in a single index lookup.
 */
export const WORK_ITEM_LINK_CONTEXTS = [
    'related', // ↔ related        (symmetric)
    'blocks', // → blocked_by
    'blocked_by', // → blocks
    'duplicates', // → duplicated_by
    'duplicated_by', // → duplicates
    'caused_by', // → causes
    'causes', // → caused_by
];
export const WORK_ITEM_LINK_MIRROR = {
    related: 'related',
    blocks: 'blocked_by',
    blocked_by: 'blocks',
    duplicates: 'duplicated_by',
    duplicated_by: 'duplicates',
    caused_by: 'causes',
    causes: 'caused_by',
};
// ctsk-062 — Document↔document link contexts. Same direction-aware
// model as WORK_ITEM_LINK_CONTEXTS: each direction is its own row in
// document_links so queries from either side are a single index
// lookup, and a mirror map keeps the inverse insert atomic.
export const DOCUMENT_LINK_CONTEXTS = [
    'related', // ↔ related        (symmetric)
    'supersedes', // → superseded_by
    'superseded_by', // → supersedes
    'references', // → referenced_by  (doc A cites doc B)
    'referenced_by', // → references
    'duplicates', // → duplicated_by
    'duplicated_by', // → duplicates
    // 0.1.21 (Stage 1 of document-relationships FR) — crawl tree
    // contexts for hyper-scanno + future crawlers. parent_of sits
    // ON THE PARENT and points at the child; "find children of X"
    // is `WHERE from = X AND context = 'parent_of'`. Inverse
    // child_of lets the child carry a back-reference for fast
    // "find my parent" queries.
    'parent_of', // → child_of       (this doc is parent OF target)
    'child_of', // → parent_of      (this doc is child OF target)
    'sibling', // ↔ sibling        (peer pages in same crawl)
    'seed_of', // → seeded_from    (this doc seed OF the crawl)
    'seeded_from', // → seed_of        (this doc was seeded FROM)
    // 0.1.25 (Stage 3) — asset / attachment relationships. Use
    // these when ONE doc depends on or is generated from another:
    // markdown ↔ image, PDF ↔ extracted MD/images, batch-ingest
    // group siblings.
    'attaches', // → attached_to    (this MD attaches an image; this PDF attaches a cover)
    'attached_to', // → attaches       (this image is attached_to the MD)
    'extracts_to', // → extracted_from (this PDF extracts_to its MD + images)
    'extracted_from', // → extracts_to    (this MD was extracted_from the PDF)
    'peer_of', // ↔ peer_of        (same-batch ingest peers; symmetric)
];
export const DOCUMENT_LINK_MIRROR = {
    related: 'related',
    supersedes: 'superseded_by',
    superseded_by: 'supersedes',
    references: 'referenced_by',
    referenced_by: 'references',
    duplicates: 'duplicated_by',
    duplicated_by: 'duplicates',
    parent_of: 'child_of',
    child_of: 'parent_of',
    sibling: 'sibling',
    seed_of: 'seeded_from',
    seeded_from: 'seed_of',
    attaches: 'attached_to',
    attached_to: 'attaches',
    extracts_to: 'extracted_from',
    extracted_from: 'extracts_to',
    peer_of: 'peer_of',
};
// Human-readable labels for the UI dropdown / pill. Keep terse so
// they fit inside chip styling.
export const DOCUMENT_LINK_LABELS = {
    related: 'Related',
    supersedes: 'Supersedes',
    superseded_by: 'Superseded by',
    references: 'References',
    referenced_by: 'Referenced by',
    duplicates: 'Duplicates',
    duplicated_by: 'Duplicate of',
    parent_of: 'Parent of',
    child_of: 'Child of',
    sibling: 'Sibling',
    seed_of: 'Seed of',
    seeded_from: 'Seeded from',
    attaches: 'Attaches',
    attached_to: 'Attached to',
    extracts_to: 'Extracts to',
    extracted_from: 'Extracted from',
    peer_of: 'Peer of',
};
export const CUSTOM_PROPERTY_ENTITIES = ['document', 'work_item'];
/**
 * Cross-org visibility for shareable resources (documents, work_items,
 * projects). Locked semantics (see task #90 + docs/coo-handoff.md):
 *
 *   - `private` (default): row is readable only from inside its owning
 *     org. Every new row starts here.
 *   - `public`: row is readable from any org the caller is a member of.
 *     Mutations are STILL restricted to the owning org — a public flag
 *     does NOT grant write access. Read predicate becomes
 *     `org_id = $active_org OR (visibility = 'public' AND deleted_at IS NULL)`.
 *
 * Stored as `varchar(16)` rather than a postgres enum so the column can
 * grow new states later (e.g. explicit `shared_with_orgs` list) without
 * a DDL migration. Application validation lives in `VisibilityEnum` in
 * the schemas package.
 */
export const VISIBILITY_VALUES = ['private', 'public'];
// Maps a default work status to its workflow stage (D-08 — derived, not stored on the row)
export const STATUS_TO_STAGE = {
    backlog: 'backlog',
    spec_needed: 'backlog',
    todo: 'unstarted',
    ready: 'unstarted',
    waiting_on_decision: 'unstarted',
    in_progress: 'started',
    in_review: 'started',
    needs_iteration: 'started',
    blocked: 'started',
    done: 'completed',
    verified: 'completed',
    cancelled: 'cancelled',
    deferred: 'cancelled',
};
/**
 * CTSK-180 (2026-07-24) — Statuses whose `STATUS_TO_STAGE` maps to a
 * terminal workflow stage (`completed` or `cancelled`). Derived from
 * STATUS_TO_STAGE so adding a new terminal status is a one-line change
 * in that map and this list updates automatically.
 *
 * Use as the single source of truth for any "hide done", "show open
 * only", or "not-yet-completed" predicate — client OR server. Avoids
 * the 3-place hardcoding anti-pattern that made the original Hide-done
 * filter miss `verified` / `cancelled` items.
 *
 * At time of writing (2026-07-24): ['done', 'verified', 'cancelled', 'deferred'].
 */
export const TERMINAL_WORKITEM_STATUSES = Object.entries(STATUS_TO_STAGE)
    .filter(([, stage]) => stage === 'completed' || stage === 'cancelled')
    .map(([status]) => status);
/**
 * CTSK-243 (re-shipped 2026-08-18) — the "stale" threshold in days. A work
 * item is considered stale when its updatedAt is older than this many days
 * ago. Single source of truth for the Stale quick-filter (server-side
 * predicate on GET /workitems) and any UI badge/chip that shades stale rows.
 *
 * Was originally in the CTSK-243 CHANGELOG entry but the constant never
 * actually landed in shared until this re-ship (CTSK-388 refactor of
 * WorkItemListQuerySchema dropped it silently along with the query fields
 * that referenced it — the follow-up commit only recovered the API+web
 * files, not the shared additions). Re-added here + wired into the route
 * + schema in the same commit so the failing integration test finally
 * passes.
 */
export const STALE_WORKITEM_DAYS = 14;
export const ACCEPTED_UPLOAD_MIME_TYPES = [
    // Documents
    'application/pdf',
    'text/markdown',
    'text/plain',
    'text/html',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    // Structured text (#117)
    'text/yaml',
    'application/yaml',
    'application/x-yaml',
    'application/json',
    'application/xml',
    'text/xml',
    // Tabular text (#117)
    'text/csv',
    'text/tab-separated-values',
    // Source code (#117) — browsers send these inconsistently; the
    // `text/plain` fallback above still catches drops where the
    // browser couldn't classify (.sh on Linux often arrives as plain).
    'text/javascript',
    'application/javascript',
    'application/typescript',
    'text/x-typescript',
    'text/x-python',
    'application/x-python',
    'application/x-sh',
    'application/x-shellscript',
    // #124 — log files. Browsers send .log inconsistently (often
    // text/plain — already in the list — but occasionally
    // application/x-log or application/octet-stream).
    'application/x-log',
    // Images
    'image/png',
    'image/jpeg',
    'image/tiff',
    'image/gif',
    'image/webp',
    'image/svg+xml',
];
export const MAX_UPLOAD_BYTES = 200 * 1024 * 1024; // 200 MB
// Default capability list (spec §04). Roles bundle capabilities from this set.
export const CAPABILITIES = [
    'documents:view', 'documents:view_all', 'documents:create', 'documents:edit',
    'documents:edit_all', 'documents:delete', 'documents:restore', 'documents:purge',
    'documents:share', 'documents:reprocess', 'documents:bulk', 'documents:export',
    'documents:view_deleted',
    // 0.1.55 — CTSK-166 Phase C. Narrow self-scoped read used by external
    // clients polling for their own access-request state via
    // GET /nests/:slug/access-requests. Filters by
    // custom_properties.source_tag matching the caller's PAT sourceTag,
    // scoped to a single nest slug (default: external-clients). Cannot
    // enumerate other docs, cannot cross the nest boundary. NOT bundled
    // into any DEFAULT_ROLES — intended for bootstrap-external-apply
    // (custom-tier) PATs only. See docs/pats-api.md §8.5 (Phase C).
    'documents:view_own_access_request',
    // CTSK-509 — lightweight "post-it" notes (docs/notes.md). Own family, NOT a
    // reuse of documents:edit / workitems:edit, so notes never widen document or
    // work-item permissions. `notes:edit` / `notes:delete` are author-only: the
    // route also checks note.author_id === session user (403), the capability
    // alone is not enough. No `notes:edit_all` in the MVP. Bundled into owner
    // (all caps) + operator (not on its exclusion list); the explicit-allowlist
    // roles (reviewer/contributor/viewer/...) do not get them.
    'notes:view', 'notes:create', 'notes:edit', 'notes:delete',
    'sidecars:view', 'sidecars:edit', 'sidecars:override_protected',
    'tags:view', 'tags:manage',
    'nests:view', 'nests:manage',
    // CTSK-312 Slice 1 (ADR 0002 §3 Q2) — a categorically different power
    // from `nests:manage` (which is "rename a nest, set its landing
    // path"): this cap gates defining what authority may be MINTED into
    // a nest via a non-empty `nestIntegrations:` bundle block (importer
    // path lands Slice 2). Deliberately its own string, not folded into
    // `nests:manage` — ADR §3 Q2 Control 1. Granted to `owner` (via the
    // `[...CAPABILITIES]` spread below) and `operator` (via the
    // CAPABILITIES.filter() pattern — not on operator's exclusion list)
    // automatically; reviewer/contributor/viewer/emissary/steward/envoy
    // are explicit allowlists that do NOT include it, so no DEFAULT_ROLES
    // edit is needed to keep this owner+operator-only (ratified §8 R2).
    'nests:manage_integrations',
    'tagging_rules:view', 'tagging_rules:manage',
    'custom_properties:view', 'custom_properties:manage',
    // work-item read is split (0.1.39) — `workitems:list` gates the
    // collection route (`GET /workitems`) so a PAT with just the list
    // cap can support observability tiers without also getting the
    // detail read cap. `workitems:view` retains its meaning but its
    // scope narrows to detail routes (`GET /workitems/:id`). Every role
    // bundle that carried `workitems:view` also gains `workitems:list`
    // to preserve list-access; see DEFAULT_ROLES below and
    // docs/proposals/0.1.39-pat-scope-tiers.md §4.1 for rationale.
    'workitems:list', 'workitems:view', 'workitems:create', 'workitems:edit', 'workitems:delete',
    'workitems:transition', 'workitems:transition:review', 'workitems:assign',
    'workitems:bulk',
    // Projects + comments finer-grained reads (0.1.39). Introduced
    // to tighten PAT tier boundaries; the read-only tier lists
    // projects + reads comments without inheriting `workitems:view`.
    // Route gates: GET /projects[/:id] → projects:view;
    //              GET /workitems/:id/comments → comments:view;
    //              POST /workitems/:id/comments → comments:create.
    'projects:view', 'comments:view', 'comments:create',
    // CTSK-372 — body-only comment mutation. Split author/admin the same
    // way documents:edit / documents:edit_all is split: `comments:edit_own`
    // gates PATCH /workitems/:id/comments/:commentId when the caller is
    // comment.authorId; `comments:edit_any` is the admin override for
    // everyone else. Route gate is dynamic (checked in-handler against the
    // fetched comment row), not a static requireCapability preHandler —
    // same pattern as the existing workitems:author_override check a few
    // lines below. `comments:edit_any` is deliberately paired with
    // `settings:manage` in role bundles below (owner/operator inherit both
    // automatically; reviewer/contributor/viewer, which hold settings:view
    // only, get neither).
    'comments:edit_own', 'comments:edit_any',
    // External-importer affordances. Author override lets the caller set
    // comments.author_id to another user (so a bulk re-import preserves
    // historic authors). Bulk import enables POST /workitems/bulk.
    // Timestamp override lets the caller set createdAt/updatedAt/completedAt
    // on work items + createdAt on comments (so historic items keep their
    // dates instead of being stamped at import time).
    'workitems:author_override', 'workitems:bulk_import', 'workitems:timestamp_override',
    // Soft cross-link management (POST /workitems/:id/links/workitems).
    'workitems:link',
    // Cross-org visibility toggles. Required to call
    // PATCH /workitems|projects/:id/visibility. The documents counterpart
    // (`documents:share`) already exists above. Bundled into owner + operator
    // by default; reviewers/contributors can read public items but not flip
    // ownership of the visibility state.
    'workitems:share', 'projects:share',
    // Project lifecycle (0.1.7 / RBAC pass). Splits the org-owner's "I can
    // do anything in my org" into fine-grained caps so per-role bundles
    // can grant project mutation without granting work-item mutation
    // (or vice-versa).
    'projects:create', 'projects:archive',
    // CTSK-449 — reassign a project's owner via PATCH /projects/:id
    // { ownerId }. Deliberately NOT bundled into the session-scope
    // ROLE_CATALOG handles (basic-workitems, workitem-reviewer):
    // ownership transfer is an operator-tier decision. Lands in
    // owner (all caps) + operator (not on its exclusion list) via
    // DEFAULT_ROLES below.
    'projects:transfer_ownership',
    'workflows:view', 'workflows:configure',
    'tasks:view', 'tasks:retry', 'tasks:cancel', 'tasks:dismiss',
    'pipelines:view', 'pipelines:run', 'pipelines:configure',
    'settings:view', 'settings:manage', 'settings:manage_security',
    // Org lifecycle + membership management (0.1.7 / RBAC pass). Each
    // formerly required `system:admin`; splitting them lets cross-org
    // roles (emissary, steward, envoy) act on orgs without inheriting
    // every other admin power.
    //   orgs:view           — list/read orgs visible to the caller.
    //                         Gates GET /orgs and GET /orgs/:id.
    //   orgs:create         — POST /orgs. Mint a new tenant.
    //   orgs:archive        — DELETE /orgs/:id (soft-delete is_active=false)
    //                         AND POST /orgs/:id/archive (set archived_at).
    //   orgs:hide           — PATCH /orgs/:id/visibility (toggle is_hidden).
    //                         Pure UI preference; org keeps functioning.
    //   orgs:manage_members — POST/PATCH/DELETE /orgs/:id/memberships.
    //                         Per-org `isOwner` on the same org also satisfies
    //                         the check (so a per-org owner doesn't need a
    //                         global cap to manage their own org's members).
    'orgs:view', 'orgs:create', 'orgs:archive', 'orgs:hide', 'orgs:manage_members',
    // Tools (0.1.8+, migration 0019). A tool is a first-class extension
    // bound to a nest with a dedicated storage container; ships seed
    // defaults (tags/rules/workflows/custom-properties) on install.
    //   tools:view           — list & read /tools.
    //   tools:install        — POST /tools, DELETE /tools/:id.
    //                          Held by emissary / steward / envoy (so a
    //                          platform operator can roll out tools
    //                          cross-org) + owner / operator.
    //   tools:configure      — PATCH /tools/:id (rename, settings,
    //                          version bump, registry_public toggle).
    //   tools:read_registry  — GET /tools/:id/registry. Cross-tool
    //                          access via the registry_public flag or
    //                          explicit grants in tool_registry_access.
    //   tools:write_registry — POST /tools/:id/registry. Writing the
    //                          canonical file is reserved for the tool's
    //                          owning role per-org owner / operator.
    'tools:view', 'tools:install', 'tools:configure',
    'tools:read_registry', 'tools:write_registry',
    // FR 2026-05-31 — Tool invocation framework.
    //   tools:invoke — POST /tools/:slug/invoke. Lets a caller fire a
    //                  manifest-declared `verb: 'invoke'` action on a
    //                  document. Per-action requiredCaps[] in the
    //                  manifest stack ON TOP of this baseline so a
    //                  tool whose action mutates documents can also
    //                  demand `documents:update`.
    // tools:invoke_async lands when async ships in v2.
    'tools:invoke',
    'users:view',
    // users:create is a strict subset of users:manage. Bundled into
    // emissary / steward so a cross-org operator can mint a new user
    // without inheriting password-reset / disable / role-assign powers.
    'users:create',
    'users:manage',
    // Personal Access Tokens (0.1.37 / migration 0028).
    //   pats:manage — list/mint/revoke own tokens. Self-scoped: every
    //                 authenticated user holds this implicitly via the
    //                 route layer (no role bundle required); the cap
    //                 exists so a future `pats:admin` can layer on top
    //                 for users:manage holders to revoke others' tokens.
    'pats:manage',
    // 0.1.5X — CTSK-166 Phase A4 — External Clients Nest approval flow.
    //   pats:mint_from_approval — hold to run the approve_and_mint_pat
    //                             workflow action from the tool palette.
    //                             Subsettable from pats:manage; distinct so
    //                             an operator can grant "can approve external
    //                             client requests" without granting full PAT
    //                             admin (revoke arbitrary tokens etc.). Per
    //                             COO decision: held by owners of the
    //                             Mission Control org (role-based, portable
    //                             across owner changes).
    'pats:mint_from_approval',
    // 0.1.55 — CTSK-166 Phase D — deny counterpart to
    // pats:mint_from_approval. Held by the same role (Mission Control
    // owner) per COO P4b. Gates POST /documents/:id/actions/deny-
    // access-request. No mint side-effect; swaps access/pending →
    // access/denied and stamps denial_reason CP + denied_at +
    // denied_by_user_id.
    'pats:deny_access_request',
    // 0.1.5X — CTSK-170 Part A/4 — policy cap for the change_client_role
    // workflow action. Held by the same role bundle as mint/deny — the
    // Mission Control org owner (COO P4b). Not in operator's bundle:
    // elevate/downgrade/sidegrade decisions are policy calls, not day-
    // to-day operator work.
    'pats:change_client_role',
    // 0.1.57 — CTSK-181 — revoke an approved client's PAT and transition
    // the doc to `access/revoked`. Terminal in the access-state graph
    // (cannot be un-revoked; a fresh access request re-mints). Held by
    // the same role bundle as mint/deny/change-role per COO P4b policy —
    // revocation is an org-owner decision, not day-to-day operator work.
    'pats:revoke_client_pat',
    // 0.1.58 — CTSK-194 — org-wide visibility into all access-request
    // docs in a nest, regardless of the caller's sourceTag. Gates the
    // admin-scoped GET /nests/:slug/access-requests/admin endpoint that
    // powers the Mission Control External Clients Nest page for cookie-
    // auth operators + PAT holders of the `pats-operator` role.
    //
    // Read-only: does NOT grant approve/deny/change-role/revoke — those
    // remain gated on their own caps (pats:mint_from_approval etc.).
    // Held by owner (via [...CAPABILITIES]) + operator (default; not in
    // the operator-exclusion list) + the new `pats-operator` role.
    //
    // Distinct from documents:view_own_access_request — that one is
    // caller-scoped by sourceTag; this one is org-scoped and returns
    // every access request in the nest.
    'pats:view_all',
    // 0.1.5X — CTSK-170 Part A — narrow read cap for the role catalog
    // discovery endpoint (GET /api/v1/role-catalog). Added to the bootstrap
    // PAT scope so a fresh client can `tasko_list_roles` before picking
    // a role handle for `tasko_request_access`. Catalog IS public
    // metadata by design; this cap keeps the auth model tidy without
    // materially widening the bootstrap PAT's attack surface.
    'roles:list',
    'groups:manage',
    'roles:manage',
    'audit:view', 'audit:export',
    // CTSK-333 (Phase 3 of EPIC CTSK-385) — Recycle Bin. Split view/purge
    // into two caps on purpose (same pattern as `pats:view_all` vs the
    // mint/revoke caps below): an operator can be granted "see what's in
    // the recycle bin" without also getting the destructive hard-delete
    // power. `admin:view_deleted` gates the Recycle Bin UI + its list
    // reads; `admin:purge_deleted` additionally gates the 6 new
    // `POST /<resource>/:id/actions/purge` endpoints. Deliberately
    // separate from each resource's own pre-existing `documents:purge`-
    // style cap (see documents.ts's `DELETE /documents/:id?force=true`
    // path) — that's a narrower, resource-specific mechanism that
    // predates this EPIC and is left as-is; the new endpoints are the
    // uniform, admin-gated, confirmSlug-guarded surface for all 6
    // resource types, soft-deleted rows only.
    'admin:view_deleted', 'admin:purge_deleted',
    // CTSK-447 — gates the `before`/`after`/`diff` CONTENT payload
    // (`resource_audit.changes_json`) on `GET /audit` for rows whose
    // target is currently soft-deleted. Deliberately verbose name
    // (`_soft_deleted_content`, not `_deleted_content`) so it can never
    // be misread as also covering purged (hard-deleted) targets — it
    // does NOT: purged-target payloads are permanently redacted for
    // EVERY caller, this cap or no cap (see audit.ts route + migration
    // 0058). `audit:view` alone still returns full metadata (actor,
    // timestamp, eventType, resourceId) for every row, and full payload
    // for LIVE (non-deleted, non-purged) targets — this cap only narrows
    // the one case where plain `audit:view` used to bypass
    // `admin:view_deleted` by reading a soft-deleted resource's content
    // through its own history instead of the resource endpoint.
    //
    // POLICY — CTSK-450 (Option A ratified 2026-08-27): EXPLICIT-ASSIGN-ONLY.
    // Holders as of 0.1.230:
    //   - `owner` — inherits via `[...CAPABILITIES]` full-spread (intentional
    //     carve-out; owner is the "sees everything" role by design).
    //   - `pats-admin` (PAT role) — explicit grant from CTSK-447 ship.
    //   - `operator` (org role) — EXCLUDED. Added to operator's exclusion
    //     list in DEFAULT_ROLES.operator below (line ~889) so it can't
    //     auto-inherit via the CAPABILITIES.filter() pattern.
    //   - All other bundles: must add explicitly. Do NOT auto-grant based
    //     on `admin:view_deleted` presence or any other proxy — that
    //     contradicts CTSK-438's "explicit is better than implicit"
    //     principle. Change this policy only via a filed ticket + update
    //     to this comment block + shared-test update.
    'audit:view_soft_deleted_content',
    'system:admin',
];
// ─── Higher-risk custom-PAT cap allowlist (CTSK-456) ───────────────────
//
// Part of the quarterly custom-tier PAT review runbook (CTSK-431 §6
// Decision 4; see docs/runbooks/custom-pat-review.md). A custom-tier
// PAT whose `scopes[]` includes ANY capability in this set is flagged
// in the runbook checklist as "higher risk" — the ratified policy text
// says such tokens "may require a shorter cadence" than the default
// 90-day quarterly maximum. This ship does NOT enforce a shorter
// cadence automatically (no code reads this set yet); it's the
// declared reference list a human reviewer checks a PAT's scopes
// against during the runbook's review step. Automating the shorter-
// cadence enforcement is a candidate fast-follow once the manual
// process has run a cycle or two.
//
// Membership rationale (proposed set, confirm/adjust at review time
// per the ticket body):
//   - `admin:purge_deleted` — irreversible hard-delete across all 6
//     resource types.
//   - `settings:manage_security` — org-wide security-relevant settings.
//   - `pats:mint_from_approval` — can mint new PATs for other users via
//     the approval flow (privilege-propagation risk).
//   - `audit:view_soft_deleted_content` — EXPLICIT-ASSIGN-ONLY per
//     CTSK-450; reads content of soft-deleted resources.
export const HIGHER_RISK_CUSTOM_PAT_CAPS = new Set([
    'admin:purge_deleted',
    'settings:manage_security',
    'pats:mint_from_approval',
    'audit:view_soft_deleted_content',
]);
/**
 * Seed catalog. Order preserved as displayed in the picker UI.
 *
 * `basic-workitems` first because it's the default recommended set for
 * Cowork Claude sessions — the 14-cap tier-update envelope adopted
 * 2026-07-03. Every one of the 15 Round-1/2 session PATs backfilled
 * into the Nest via Phase D (`scripts/backfill-session-registrations.ts`,
 * commit 2aaf555) uses this exact set.
 *
 * `custom` last because it's the escape hatch — a client that doesn't
 * fit any preset can request specific caps via the existing
 * `requestedCapabilities` array (backward-compat path).
 */
export const ROLE_CATALOG = [
    {
        handle: 'basic-workitems',
        description: 'Standard Cowork Claude session envelope. Full workitem lifecycle EXCEPT closing reviews — can move tickets through backlog → ready → todo → in_progress → in_review (the primary work flow), comment, edit their own comments, link, assign. Cannot transition in_review → done or → verified — those require workitems:transition:review, which lives in the workitem-reviewer role instead. Rationale (2026-07-18 split): review approval is a policy decision that belongs at the reviewer tier, not baked into every work-doer session. The 15-cap set (13 + comments:edit_own added 2026-08-15, CTSK-372 — author-scoped comment edit is baseline for a session that can already comment; + roles:list added 2026-08-26, CTSK-429 — add-without-broaden so a session that swaps from bootstrap to this handle retains catalog-discovery access instead of losing it). NOT comments:edit_any — the admin override stays paired with settings:manage at the owner/operator tier.',
        capabilities: [
            'workitems:view', 'workitems:list', 'workitems:create', 'workitems:edit',
            'workitems:transition',
            'workitems:link', 'workitems:assign',
            'comments:create', 'comments:view', 'comments:edit_own',
            'documents:view', 'tags:view', 'projects:view', 'workflows:view',
            'roles:list',
        ],
        version: '2026-08-26',
        category: 'session',
    },
    {
        handle: 'workitem-reviewer',
        description: 'Superset of basic-workitems — same 15 caps PLUS workitems:transition:review. Can approve in_review → done AND transition to verified. Intended for reviewer sessions (both those that do their own work + close their own reviews, and reviewer-only sessions that primarily sign off on others\' work). Introduced 2026-07-18 alongside the basic-workitems verification-split — pre-split, basic-workitems held all 14 caps as a single "session envelope." Now the split makes the "who can close" decision explicit at mint time. Gained comments:edit_own 2026-08-15 (CTSK-372) and roles:list 2026-08-26 (CTSK-429) alongside basic-workitems to keep the superset relationship exact.',
        capabilities: [
            'workitems:view', 'workitems:list', 'workitems:create', 'workitems:edit',
            'workitems:transition', 'workitems:transition:review',
            'workitems:link', 'workitems:assign',
            'comments:create', 'comments:view', 'comments:edit_own',
            'documents:view', 'tags:view', 'projects:view', 'workflows:view',
            'roles:list',
        ],
        version: '2026-08-26',
        category: 'session',
    },
    {
        handle: 'read-only-observer',
        description: 'View-only across workitems + comments + docs + metadata. Cannot create, edit, transition, comment, or write anything. Suitable for dashboards, external monitors, read-only integrations.',
        capabilities: [
            'workitems:view', 'workitems:list',
            'comments:view',
            'documents:view', 'tags:view', 'projects:view', 'workflows:view',
        ],
        version: '2026-07-11',
        category: 'session',
    },
    {
        handle: 'session-bootstrap',
        description: 'Fallback credential tasko-mcp preinstalls in-memory (CTSK-218) for any session — cloud (`cse_...`) or local Fable-it (`local_...`) — whose subUuidTail has no matching `.env` file at boot. Holds just enough to self-onboard: documents:create lets it call tasko_greet (Step 0) and tasko_request_access to file a real access request; documents:view_own_access_request lets it poll tasko_check_access_status for approval state; roles:list lets it discover this catalog (mirrors bootstrap-external-apply rationale below). Cannot view or touch workitems, comments, or anything else — the first write attempt beyond greet/request-access is rejected with a pointer back to tasko_request_access, and (CTSK-373, 2026-08-21) a server-side guard now enforces that rejection independent of whatever this catalog entry says, so a future catalog/DB drift cannot silently reopen the hole. Once approved and re-loaded via tasko_load_session_env, the session swaps to its real minted role and this PAT is discarded. Same capability set as bootstrap-external-apply (documents:create + documents:view_own_access_request + roles:list) but category=session since the client kind is a Cowork session, not the shared external-apply credential.',
        capabilities: [
            'documents:create', 'documents:view_own_access_request', 'roles:list',
        ],
        version: '2026-08-21',
        category: 'session',
        // CTSK-373 follow-up (2026-08-21): this is THE one shared fallback
        // identity — locking sourceTag to the handle name itself so a future
        // re-mint can't drift to a different spelling again (root cause of
        // the live bootstrap-write-guard.ts smoke-test failure the same day).
        forcedSourceTag: 'session-bootstrap',
    },
    {
        handle: 'bootstrap-external-apply',
        description: 'The shared "request-for-access" credential handed to brand-new external clients that have no PAT yet (the chicken-and-egg identity — docs/pat-minimum-scopes.md §2.1, docs/booking-flow.md §5). Previously only a custom-tier mint CONVENTION (sourceTag `bootstrap-external-apply` + hand-typed scopes), which let deployed tokens drift: custom-tier scopes freeze at mint, and pre-0.1.55 mints lacked documents:create, 403ing tasko_greet and stalling the whole onboarding chain (CTSK-325; hit by CTSK-287/321/273). Promoted to a catalog entry (CTSK-325, 0.1.137) so re-mints seed from here — self-healing. documents:create lets the holder greet + file an access request; documents:view_own_access_request lets it poll its own request state; roles:list lets it discover this catalog (CTSK-170 Part A/2). Cannot list, view, or edit any other docs.',
        capabilities: [
            'documents:create', 'documents:view_own_access_request', 'roles:list',
        ],
        version: '2026-08-10',
        category: 'custom',
    },
    {
        handle: 'comment-only-bot',
        description: 'Read-only observer plus the ability to post comments on workitems. Nothing else. For notification bots and CI status forwarders that need to leave audit trails without touching state.',
        capabilities: [
            'workitems:view', 'workitems:list',
            'comments:view', 'comments:create',
            'documents:view', 'tags:view', 'projects:view', 'workflows:view',
        ],
        version: '2026-07-11',
        category: 'ci-bot',
    },
    {
        handle: 'ingest-only-webhook',
        description: 'Post new documents into scoped Nests only. Self-scoped read via documents:view_own_access_request. For external systems producing artifacts (report generators, hyper-scanno crawls) without needing broader read.',
        capabilities: [
            'documents:create', 'documents:view_own_access_request',
        ],
        version: '2026-07-11',
        category: 'webhook',
    },
    {
        handle: 'doc-writer',
        description: 'Read-only observer PLUS documents:create + documents:edit for scoped Nest work. Suitable for content ingest daemons that both write and revise docs (e.g. a report generator that updates status docs).',
        capabilities: [
            'workitems:view', 'workitems:list',
            'comments:view',
            'documents:view', 'documents:create', 'documents:edit',
            'tags:view', 'projects:view', 'workflows:view',
        ],
        version: '2026-07-11',
        category: 'webhook',
    },
    {
        handle: 'pats-operator',
        description: 'Read-only visibility into org-wide access-request docs in the External Clients Nest. Holds pats:view_all + documents:view. Intended for admin dashboards + tooling that surfaces the full access-request state across every external client, regardless of the caller\'s sourceTag. Does NOT grant approve/deny/change-role/revoke — those remain gated on their own caps and require the Mission Control org owner role. Introduced 2026-07-22 (CTSK-194) to unblock the Mission Control admin view when accessed via a non-cookie session; cookie owner/operator sessions get pats:view_all automatically via their per-org role.',
        capabilities: [
            'pats:view_all',
            'documents:view',
        ],
        version: '2026-07-22',
        category: 'admin',
    },
    {
        handle: 'pats-admin',
        description: 'Delegated PAT-lifecycle write authority — approve access requests, deny with reason, change client role, revoke — without requiring cookie-authenticated org-owner. Holds the four PAT-mutation caps (pats:mint_from_approval, pats:deny_access_request, pats:change_client_role, pats:revoke_client_pat) PLUS the read side (pats:view_all + documents:view), audit:view (CTSK-389, 2026-08-18) for delegated ops PATs to inspect the resource_audit trail of the client-role changes they perform, and admin:view_deleted + admin:purge_deleted (CTSK-333 Phase 3, 2026-08-18) for the Recycle Bin UI + hard-purge endpoints. Establishes an operator-ladder convention with pats-operator: pats-operator (read-only visibility) < pats-admin (write authority). Introduced 2026-07-23 (CTSK-205, item 4 of CTSK-195 epic) to complete the read/write role split that CTSK-194\'s pats-operator started. Use for delegated ops PATs, admin dashboards that also mutate, or any non-owner identity that needs the full PAT lifecycle authority. Does NOT include workitems:* / projects:* / users:* / system:admin — pure PAT-lifecycle role, cross-orthogonal to workitem-reviewer and basic-workitems.',
        capabilities: [
            'pats:mint_from_approval',
            'pats:deny_access_request',
            'pats:change_client_role',
            'pats:revoke_client_pat',
            'pats:view_all',
            'documents:view',
            // CTSK-389 — lets a pats-admin holder inspect the resource_audit
            // trail for the mutations this role can perform. audit:view was
            // already a CAPABILITIES entry + DEFAULT_ROLES member (owner /
            // operator / reviewer / emissary / steward / envoy) from
            // CTSK-386/387 prep work; this ticket's scope item 1 just adds it
            // to the pats-admin catalog handle per the brief.
            'audit:view',
            // CTSK-333 (Phase 3 of EPIC CTSK-385, 2026-08-18) — Recycle Bin
            // view + hard-purge. Delegated ops PATs holding this role can now
            // see soft-deleted rows and (separately) hard-purge them without
            // needing system:admin or direct Postgres access — the exact gap
            // this EPIC set out to close.
            'admin:view_deleted',
            'admin:purge_deleted',
            // CTSK-470 (2026-08-31) — the CTSK-450 policy comment above (near
            // the CAPABILITIES definition of this cap) already documented
            // pats-admin as an "explicit grant from CTSK-447 ship," but the
            // grant itself was never actually added to this array — a
            // comment/array split first caught by the CTSK-462 docs audit
            // (§5.4a) and ratified for correction here. Lets a delegated
            // pats-admin PAT inspect resource_audit content for soft-deleted
            // targets, matching what the comment already claimed.
            'audit:view_soft_deleted_content',
        ],
        version: '2026-08-31',
        category: 'admin',
    },
    // ── Tool-integration roles (0.1.125, CTSK-311) ──────────────────
    // First named handles carrying tools:write_registry — pre-CTSK-311,
    // every tool-integration client (tootasko-clipper's DSR bookmark path
    // first among them) had to be minted as `custom` with hand-typed
    // capabilities. See docs/tasko-mcp-setup.md §7 for the .env emission
    // behavior tied to `tootasko-ext`.
    {
        handle: 'tootasko-ext',
        description: 'Browser extension (tootasko-clipper): Save-to-API + Save-as-DSR-Bookmark. Capabilities let it push new documents via /documents/ingest AND append entries to any tool registry file via /tools/:id/registry/append. Enumerates installed tools at startup (tools:view) so the extension can auto-discover the DSR tool id instead of hard-coding it in config.',
        capabilities: [
            'documents:create', // POST /documents/ingest
            'tools:view', // GET /tools — auto-discover DSR tool id at extension startup (operator amendment #2, 2026-08-06)
            'tools:write_registry', // POST /tools/:id/registry/append
        ],
        version: '2026-08-06',
        category: 'browser-extension',
    },
    {
        handle: 'registry-reader',
        description: 'Read-only tool-registry consumer. Analytics daemon, external mirror, GitOps sync-out. Reads registry files but never writes.',
        capabilities: [
            'tools:view',
            'tools:read_registry',
        ],
        version: '2026-08-06',
        category: 'webhook',
    },
    {
        handle: 'registry-writer',
        description: 'Read/write/append tool-registry client. Any external system that needs full round-trip on a specific tool registry file. Superset of registry-reader; distinct from tootasko-ext (no documents:create).',
        capabilities: [
            'tools:view',
            'tools:read_registry',
            'tools:write_registry',
        ],
        version: '2026-08-06',
        category: 'webhook',
    },
    {
        handle: 'custom',
        description: 'Escape hatch — the client supplies an explicit requestedCapabilities array. Operator audits every string during approval. Prefer a named role when possible.',
        capabilities: [], // caller supplies via requestedCapabilities[]
        version: '2026-07-11',
        category: 'custom',
    },
];
/**
 * Convenience lookup — resolve a handle to its definition. Returns null
 * on unknown handles so callers can distinguish "role not in catalog"
 * from "role deprecated" without hitting the DB.
 */
export function getRoleByHandle(handle) {
    return ROLE_CATALOG.find((r) => r.handle === handle) ?? null;
}
// ─── Superset/reference/exclusion invariants (CTSK-454, CTSK-431 §6 Decision 2) ──
//
// Declarative cross-checks over ROLE_CATALOG, enforced by
// `packages/shared/src/__tests__/role-catalog-invariants.test.ts`. Three
// relationship kinds, each exercised by a different subset of the 5 tests:
//
//   - `superset`  — `child.capabilities` MUST be a strict-or-equal subset of
//     `parent.capabilities` (subset test). Also fed into the cycle test's
//     DAG and the inherited-sensitive-scope test, which requires every
//     `HIGHER_RISK_CUSTOM_PAT_CAPS` member present in `parent` to also be
//     present in `child` — so `superset` is reserved for pairs where the
//     parent's *extra* caps over the child are all non-sensitive. Do NOT
//     use `superset` for an admin/read-tier split where the parent
//     deliberately holds sensitive caps the child must NOT inherit (e.g.
//     pats-admin/pats-operator below) — that legitimately fails test 5,
//     use `reference` instead.
//   - `reference`  — a documented, non-hierarchical relationship between
//     two handles (e.g. deliberately-identical capability sets, or an
//     intentional read/write split where sensitivity must NOT flow
//     through). Only checked for handle existence (reference test) — not
//     subject to subset/cycle/sensitive-scope.
//   - `exclusion`  — `parent.capabilities` and `child.capabilities` MUST be
//     disjoint (exclusion test). For pairs that must never accumulate
//     overlapping authority even if minted together.
//
// Every entry's `parent`/`child` MUST reference an existing ROLE_CATALOG
// handle (reference test) — a typo'd handle fails loudly instead of
// silently no-op'ing.
export const ROLE_SUPERSET_RELATIONSHIPS = [
    {
        parent: 'workitem-reviewer',
        child: 'basic-workitems',
        relationship: 'superset',
        rationale: 'workitem-reviewer = basic-workitems ∪ {workitems:transition:review} (CTSK-429).',
    },
    {
        parent: 'basic-workitems',
        child: 'read-only-observer',
        relationship: 'superset',
        rationale: 'basic-workitems = read-only-observer ∪ {create/edit/transition/link/assign/comment-write/roles:list}.',
    },
    {
        parent: 'basic-workitems',
        child: 'comment-only-bot',
        relationship: 'superset',
        rationale: 'basic-workitems = comment-only-bot ∪ {create/edit/transition/link/assign workitem caps + comments:edit_own + roles:list}.',
    },
    {
        parent: 'doc-writer',
        child: 'read-only-observer',
        relationship: 'superset',
        rationale: 'doc-writer = read-only-observer ∪ {documents:create, documents:edit}.',
    },
    {
        parent: 'registry-writer',
        child: 'registry-reader',
        relationship: 'superset',
        rationale: 'registry-writer = registry-reader ∪ {tools:write_registry} (documented inline on both handles since CTSK-311).',
    },
    {
        parent: 'pats-admin',
        child: 'pats-operator',
        relationship: 'reference',
        rationale: 'pats-admin’s read caps (pats:view_all, documents:view) mirror pats-operator’s full set, but pats-admin also holds pats:mint_from_approval — a HIGHER_RISK_CUSTOM_PAT_CAPS member — which must NOT flow to pats-operator by design (operator-ladder split, CTSK-194/CTSK-205). Declared `reference`, not `superset`, so the sensitive-scope invariant does not wrongly demand parity.',
    },
    {
        parent: 'session-bootstrap',
        child: 'bootstrap-external-apply',
        relationship: 'reference',
        rationale: 'Identical capability sets (documents:create, documents:view_own_access_request, roles:list) by design — same bootstrap envelope under two categories (session vs custom). Not a hierarchy, so `reference` rather than `superset`.',
    },
    {
        parent: 'registry-reader',
        child: 'pats-admin',
        relationship: 'exclusion',
        rationale: 'A tool-registry read client must never accumulate PAT-lifecycle admin authority — the two capability sets share nothing today and must stay disjoint.',
    },
];
const QUIET_PERIOD_BREAK_GLASS_CAPS = [
    'admin:purge_deleted',
    'audit:view_soft_deleted_content',
    'system:admin',
];
const QUIET_PERIOD_PRIVILEGED_CAPS = CAPABILITIES.filter((cap) => HIGHER_RISK_CUSTOM_PAT_CAPS.has(cap) && !QUIET_PERIOD_BREAK_GLASS_CAPS.includes(cap));
export const CAPABILITY_QUIET_PERIOD_CLASS = Object.fromEntries(CAPABILITIES.map((cap) => {
    if (QUIET_PERIOD_BREAK_GLASS_CAPS.includes(cap))
        return [cap, 'break_glass'];
    if (QUIET_PERIOD_PRIVILEGED_CAPS.includes(cap))
        return [cap, 'privileged'];
    return [cap, 'default'];
}));
// Default role bundles (spec §04 §4)
//
// PER-ORG roles (scope: 'org') are the existing membership model:
// owner / operator / reviewer / contributor / viewer. A user gets
// one of these per org via `organization_memberships.role_id`.
//
// GLOBAL roles (scope: 'global') are new in 0.1.7 (migration 0017).
// A user gets these via `global_role_memberships`; the role's caps
// apply across every org without per-org membership. They're meant
// for platform-operator personas (emissary), maintenance personas
// (steward), and continuity / backup roles (envoy).
export const DEFAULT_ROLES = {
    // ─── Per-org roles ──────────────────────────────────────────────
    owner: {
        scope: 'org',
        description: 'Full power within their org.',
        capabilities: [...CAPABILITIES],
    },
    operator: {
        scope: 'org',
        description: 'Day-to-day admin without irreversible / cross-org powers.',
        capabilities: CAPABILITIES.filter((c) => ![
            'documents:purge',
            'audit:export',
            'settings:manage_security',
            'users:manage',
            'groups:manage',
            'roles:manage',
            'system:admin',
            // Org lifecycle is emissary territory, not operator's.
            // Operator can still manage their own org's members
            // via the per-org owner fallback in the route auth.
            'orgs:create',
            'orgs:archive',
            'orgs:hide',
            // 0.1.5X — CTSK-166 Phase A4b — External Clients Nest approval
            // is owner-only per COO decision. Operator holds pats:manage
            // (their own tokens) but NOT pats:mint_from_approval — that
            // cap is tied to the org-owner role so it's portable across
            // owner rotations and can't be inherited by a day-to-day
            // admin without explicit owner sign-off.
            'pats:mint_from_approval',
            // 0.1.55 — CTSK-166 Phase D — deny counterpart. Same rationale
            // as mint_from_approval: rejection of an access request is a
            // policy decision that belongs at the org-owner level, not the
            // day-to-day operator.
            'pats:deny_access_request',
            // 0.1.5X — CTSK-170 Part A/4 — elevate/downgrade/sidegrade a
            // client's role. Rotates the linked PAT with the new role's
            // cap set. Same P4b policy — org-owner scope, not operator.
            'pats:change_client_role',
            // 0.1.57 — CTSK-181 — revoke an approved client's PAT +
            // transition doc to access/revoked. Terminal state. Same
            // P4b policy — org-owner scope, not day-to-day operator.
            'pats:revoke_client_pat',
            // 0.1.230 — CTSK-450 (Option A ratified 2026-08-27) —
            // audit:view_soft_deleted_content ships as EXPLICIT-ASSIGN-ONLY.
            // Excluded from operator's default cap set so this admin-tier
            // read of soft-deleted payload data can't be auto-inherited via
            // the CAPABILITIES.filter() pattern. Owner keeps it via
            // [...CAPABILITIES] spread (intentional carve-out — owner is the
            // "sees everything" role by design). Any OTHER bundle (pats-admin,
            // future admin roles, custom-tier PATs) must add it explicitly.
            // See CTSK-438 EPIC for the broader authority-model context.
            'audit:view_soft_deleted_content',
        ].includes(c)),
    },
    reviewer: {
        scope: 'org',
        description: 'Reviews content and progresses work without admin powers.',
        capabilities: [
            'documents:view', 'documents:edit', 'documents:create', 'documents:reprocess',
            'sidecars:view', 'sidecars:edit',
            'tags:view', 'nests:view', 'tagging_rules:view', 'custom_properties:view',
            // 0.1.39 split: reviewer keeps `workitems:view` for detail reads AND
            // gains `workitems:list` for collection access (regression guard —
            // every bundle that had view now has list).
            'workitems:list', 'workitems:view', 'workitems:create', 'workitems:edit',
            'workitems:transition', 'workitems:transition:review', 'workitems:assign',
            'workitems:link',
            // 0.1.39: finer-grained project + comment reads/writes.
            // CTSK-372: reviewer can edit their own comments (typo fixes) but
            // not comments:edit_any — that admin override stays with
            // settings:manage, which reviewer does not hold.
            'projects:view', 'comments:view', 'comments:create', 'comments:edit_own',
            // workflows:view is needed by the board, the work-item state picker, and
            // anywhere that renders state names — i.e. anyone who can see work items
            // needs to be able to read the workflow definitions that describe them.
            'workflows:view',
            'tasks:view', 'pipelines:view',
            // Tools — read-only. Reviewer can list tools + read registries
            // so they can interpret tagged content / referenced records.
            'tools:view', 'tools:read_registry',
            'settings:view', 'orgs:view', 'users:view', 'audit:view',
            // Self-scoped — every user manages their own PATs. Route gates
            // by user_id, so this cap on every role bundle is correct.
            'pats:manage',
        ],
    },
    contributor: {
        scope: 'org',
        description: 'Captures content and works on assigned items.',
        capabilities: [
            'documents:view', 'documents:create', 'documents:edit',
            'sidecars:view',
            'tags:view', 'nests:view', 'custom_properties:view',
            // 0.1.39 split: keep `workitems:view` for detail, add `workitems:list`
            // for collection. Preserves existing UI list behavior.
            'workitems:list', 'workitems:view', 'workitems:create', 'workitems:edit',
            'workitems:transition', 'workitems:link',
            // 0.1.39: finer-grained project + comment reads/writes.
            // CTSK-372: contributor can edit their own comments (typo fixes).
            'projects:view', 'comments:view', 'comments:create', 'comments:edit_own',
            'workflows:view',
            'tasks:view', 'pipelines:view',
            'tools:view', 'tools:read_registry',
            'settings:view', 'orgs:view', 'users:view',
            'pats:manage',
        ],
    },
    viewer: {
        scope: 'org',
        description: 'Read-only across shared documents and assigned work items.',
        capabilities: [
            'documents:view', 'sidecars:view',
            'tags:view', 'nests:view', 'custom_properties:view',
            // 0.1.39 split: viewer keeps `workitems:view` for detail reads AND
            // gains `workitems:list` for collection access.
            'workitems:list', 'workitems:view',
            // 0.1.39: viewer also gains read-only project + comment caps.
            // Not `comments:create` — viewers don't write.
            'projects:view', 'comments:view',
            'workflows:view',
            'tasks:view', 'pipelines:view',
            'tools:view', 'tools:read_registry',
            'settings:view', 'orgs:view', 'users:view',
            'pats:manage',
        ],
    },
    // ─── Global (cross-org) roles ───────────────────────────────────
    // Platform operator: full org lifecycle + cross-org member-mgmt +
    // user provisioning. Does NOT inherit content-level caps (documents,
    // work items, tags, etc.) — those still come from the active-org
    // membership. So an emissary acting inside a specific org operates
    // as whatever role their per-org membership grants (if any), PLUS
    // the org/user-management powers from being emissary.
    emissary: {
        scope: 'global',
        description: 'Cross-org platform operator: org lifecycle + members + user provisioning + tool installs.',
        capabilities: [
            'orgs:view', 'orgs:create', 'orgs:archive', 'orgs:hide', 'orgs:manage_members',
            'users:view', 'users:create',
            // Tools — emissary installs + configures (platform-operator
            // role). Doesn't get write_registry — the tool's day-to-day
            // owner manages registry contents inside their nest, even when
            // an emissary kicked off the install. Gets tools:invoke so a
            // cross-org operator can fire palette actions during routine
            // platform troubleshooting (FR 2026-05-31).
            'tools:view', 'tools:install', 'tools:configure', 'tools:read_registry',
            'tools:invoke',
            'audit:view',
        ],
    },
    // Maintenance / member-helper. No org-lifecycle powers — can't
    // create / archive / hide orgs. Can move people around and mint
    // users. Useful for delegating people-management to a support
    // role without handing over the keys to the org table itself.
    steward: {
        scope: 'global',
        description: 'Cross-org maintenance + member helper + tool installs.',
        capabilities: [
            'orgs:view', 'orgs:manage_members',
            'users:view', 'users:create',
            // Steward gets install too — useful for rolling out tools
            // across orgs as part of routine maintenance.
            'tools:view', 'tools:install', 'tools:read_registry',
            'audit:view',
        ],
    },
    // Backup admin. Same caps as emissary; different identity for
    // continuity / redundancy (if the primary emissary is unavailable
    // or you want a distinct audit trail for emergency operations).
    envoy: {
        scope: 'global',
        description: 'Backup platform operator. Same powers as emissary, distinct identity.',
        capabilities: [
            'orgs:view', 'orgs:create', 'orgs:archive', 'orgs:hide', 'orgs:manage_members',
            'users:view', 'users:create',
            'tools:view', 'tools:install', 'tools:configure', 'tools:read_registry',
            'tools:invoke',
            'audit:view',
        ],
    },
};
// Convenience: which names are global-scope? Used by the seed and
// any code that needs to distinguish role-grant flows.
export const GLOBAL_ROLE_NAMES = Object.entries(DEFAULT_ROLES)
    .filter(([, r]) => r.scope === 'global')
    .map(([name]) => name);
// Pagination defaults
export const DEFAULT_PAGE_LIMIT = 25;
// Most endpoints cap at 100; board/kanban views need higher to render every card in one go.
export const MAX_PAGE_LIMIT = 500;
// 16-token tag color palette (spec §07 §3, §11 §1.2). Hex strings.
export const TAG_COLOR_PALETTE = [
    '#2F6B4E', '#26563F', '#A56B00', '#C95F1F',
    '#B3261E', '#2A5C8C', '#5C5A52', '#8E8B81',
    '#7A4D8E', '#3B7A9B', '#B86E4D', '#6E8E3C',
    '#9C4A6E', '#4A6E9C', '#8C6E2F', '#3C8E7A',
];
export const REALTIME_EVENT_TYPES = [
    'doc.created', 'doc.updated', 'doc.deleted', 'doc.restored',
    'doc.processing_started', 'doc.processing_completed', 'doc.processing_failed',
    'doc.health_changed',
    // CTSK-387 — 'tag.restored' added alongside the new POST
    // /tags/:id/restore endpoint (tags.ts's DELETE became soft-delete in
    // the same ticket, mirroring 'doc.restored' above).
    'tag.created', 'tag.updated', 'tag.deleted', 'tag.restored',
    'work.created', 'work.updated', 'work.deleted',
    'work.status_changed', 'work.assigned',
    'task.queued', 'task.started', 'task.completed', 'task.failed',
    'pipeline.run_started', 'pipeline.run_completed', 'pipeline.run_failed',
    'user.online', 'user.offline',
];
//# sourceMappingURL=constants.js.map